import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Organization / venue scope for the Admin console.
 *
 * ── SCOPE LIVES IN THE URL ───────────────────────────────────────────────────────────
 *
 * Frozen-architecture rule: anything that changes WHAT DATA A PAGE SHOWS belongs in the
 * address bar. Scope kept only in React state cannot be linked, cannot be reloaded, and
 * cannot be reported in a bug — two people looking at "the same page" would be looking at
 * different data with no way to tell.
 *
 * So `?org=` and `?venue=` are the scope. Everything else is a convenience around them.
 *
 * ── localStorage REMEMBERS A DEFAULT, AND NOTHING MORE ───────────────────────────────
 *
 * It seeds the URL once per page load when the URL says nothing, and the URL is rewritten
 * so the address bar tells the truth about what is being shown. It never overrides a URL
 * that carries scope, and it is never read as the answer to "what scope am I in" — that
 * question has exactly one source, which is the query string.
 *
 * The remembered value distinguishes "never chosen" from "chose All Organizations" by
 * storing the ALL sentinel for the latter. Without that distinction a platform admin
 * could not express All at all: an empty `?org=` would be re-seeded with their last
 * organization on every load.
 *
 * ── THE URL IS A REQUEST, NOT A GRANT ────────────────────────────────────────────────
 *
 * Typing `?org=<someone else's org>` changes the query string and nothing else. Every
 * scoped endpoint is authorized by the engine against the caller's session; this module
 * never grants access and must never be treated as if it did.
 */

export const ORG_PARAM = 'org';
export const VENUE_PARAM = 'venue';

/** Stored when an admin deliberately chooses "All organizations". */
const ALL = 'all';
const ORG_KEY = 'voleto.admin.org';
const VENUE_KEY = 'voleto.admin.venue';

function remember(key: string, value: string | null): void {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Private mode, blocked storage, a full quota. A forgotten default is a non-event;
    // the URL is still authoritative and the console works exactly the same.
  }
}

function recall(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export interface AdminScope {
  /** null means ALL ORGANIZATIONS for a platform admin. Never "unknown". */
  organizationId: string | null;
  venueId: string | null;
  setOrganization: (id: string | null) => void;
  setVenue: (id: string | null) => void;
}

export function useAdminScope(): AdminScope {
  const [params, setParams] = useSearchParams();

  const organizationId = params.get(ORG_PARAM);
  const venueId = params.get(VENUE_PARAM);

  // Seeding is once per page load. An admin who switches to All mid-session stays there;
  // a fresh load gets their remembered default back.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (params.has(ORG_PARAM)) return;

    const rememberedOrg = recall(ORG_KEY);
    if (!rememberedOrg || rememberedOrg === ALL) return;

    const next = new URLSearchParams(params);
    next.set(ORG_PARAM, rememberedOrg);
    const rememberedVenue = recall(VENUE_KEY);
    if (rememberedVenue && !next.has(VENUE_PARAM)) next.set(VENUE_PARAM, rememberedVenue);
    // replace: seeding a default is not a navigation the back button should have to undo.
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setOrganization = useCallback((id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(ORG_PARAM, id);
    else next.delete(ORG_PARAM);
    // A venue belongs to exactly one organization, so carrying the old one across an
    // organization change would produce a combination that cannot be true.
    next.delete(VENUE_PARAM);
    remember(ORG_KEY, id ?? ALL);
    remember(VENUE_KEY, null);
    setParams(next);
  }, [params, setParams]);

  const setVenue = useCallback((id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(VENUE_PARAM, id);
    else next.delete(VENUE_PARAM);
    remember(VENUE_KEY, id);
    setParams(next);
  }, [params, setParams]);

  return { organizationId, venueId, setOrganization, setVenue };
}

/**
 * Carry the current scope onto a link.
 *
 * Used for in-console links so moving between admin screens does not silently drop the
 * organization someone selected.
 */
export function withScope(path: string, scope: { organizationId: string | null; venueId: string | null }): string {
  const q = new URLSearchParams();
  if (scope.organizationId) q.set(ORG_PARAM, scope.organizationId);
  if (scope.venueId) q.set(VENUE_PARAM, scope.venueId);
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}
